import classNames from "classnames"
import React, { useRef, useMemo } from "react"

import CellData from "./ComparisonTable/CellData"
import {
  getCellBackgroundClass,
  extractTableHeading,
} from "./ComparisonTable/utils"
import { Color } from "../../../constants/V2/color"
import Typography from "../../elements/V2/Typography"

import { TableRegex } from "@constants/tables"
import { usePageLayoutState } from "@utils/layout"
import { getAccentColor, getBackgroundColorClass } from "@utils/V2/color"

const CELL_TEXT_CLASSSES =
  "[display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2] line-clamp-2 overflow-hidden text-ellipsis"
type Props = {
  rowHeadings: string[]
  hideHeaders?: boolean
  data: string[][]
  backgroundColor: Color.Black | Color.Charcoal | Color.White | Color.MintGreen
  outerContainer: "gridSection" | "tabSelector"
  className?: string
}

const SingleEntryComparisonTable = ({
  rowHeadings,
  hideHeaders = false,
  data,
  backgroundColor,
  outerContainer,
  className,
}: Props) => {
  const { hasAnchorNavigation } = usePageLayoutState()
  const tableTheme =
    backgroundColor === Color.Black || backgroundColor === Color.Charcoal
      ? "dark"
      : "light"
  const titleRowIndices = useRef<number[]>([])
  const columnCount = useMemo(() => {
    let longestDataRow = 0
    for (const dataRow of data) {
      if (dataRow.length > longestDataRow) {
        longestDataRow = dataRow.length
      }
    }
    return longestDataRow
  }, [data])

  const actualColumnCount = Math.min(columnCount, 3)
  const maxMobileColumns = actualColumnCount
  const maxDesktopColumns = actualColumnCount

  const tableHeadingData = useMemo(
    () => extractTableHeading(data, rowHeadings),
    [data, rowHeadings]
  )

  rowHeadings.forEach((rowHeading, index) => {
    if (TableRegex.TITLE.exec(rowHeading)) {
      titleRowIndices.current.push(index)
    }
  })
  return (
    <div className={classNames(className, "relative py-8 sm:py-16")}>
      {/* Header row */}
      <div
        className={classNames(
          getBackgroundColorClass(backgroundColor),
          "z-1 sticky grid grid-flow-row items-center border-b border-gray/20 pb-8 sm:border-none",
          actualColumnCount === 2
            ? "grid-cols-2 sm:grid-cols-3"
            : "grid-cols-3 sm:grid-cols-4",
          hasAnchorNavigation || outerContainer === "gridSection"
            ? "top-60 sm:top-[65px] sm:pt-10"
            : "top-[58px] sm:top-60 lg:top-[62px]"
        )}
      >
        {/* desktop headers */}
        {tableHeadingData && (
          <div
            className={classNames(
              "hidden flex-col !items-start gap-[3px] rounded-8 px-16 py-8 sm:flex"
            )}
          >
            <Typography
              font="grotesk"
              weight="medium"
              color={getAccentColor(
                tableTheme === "light" ? Color.White : Color.Charcoal
              )}
              size="lead-sm"
              text={tableHeadingData.heading}
              className={classNames(CELL_TEXT_CLASSSES, "text-left")}
            />
            {tableHeadingData.subHeading && (
              <Typography
                font="grotesk"
                weight="book"
                color={getAccentColor(
                  tableTheme === "light" ? Color.White : Color.Charcoal
                )}
                size="body-sm"
                text={tableHeadingData.subHeading}
                className={classNames(CELL_TEXT_CLASSSES, "text-left")}
              />
            )}
          </div>
        )}

        {Array.from({ length: actualColumnCount }, (_, colIndex) => (
          <CellData
            key={`header-column-${colIndex}`}
            rowIndex={0}
            columnIndex={colIndex}
            displayIndex={colIndex}
            data={data}
            rowHeadings={rowHeadings}
            tableTheme={tableTheme}
            backgroundColor={backgroundColor}
            maxMobileColumns={maxMobileColumns}
            maxDesktopColumns={maxDesktopColumns}
            titleRowIndices={titleRowIndices}
            className="justify-center !py-8"
            variant="singleEntry"
          />
        ))}
      </div>

      {/* mobile headers */}
      {tableHeadingData && !hideHeaders && (
        <div className="flex flex-col gap-[3px] py-20 sm:hidden">
          <Typography
            font="grotesk"
            weight="medium"
            color={getAccentColor(
              tableTheme === "light" ? Color.White : Color.Charcoal
            )}
            size="body-sm"
            text={tableHeadingData.heading}
            className={classNames(CELL_TEXT_CLASSSES, "text-left")}
          />
          {tableHeadingData.subHeading && (
            <Typography
              font="grotesk"
              weight="book"
              color={getAccentColor(
                tableTheme === "light" ? Color.White : Color.Charcoal
              )}
              size="body-sm"
              text={tableHeadingData.subHeading}
              className={classNames(CELL_TEXT_CLASSSES, "text-left")}
            />
          )}
        </div>
      )}

      {/* Data rows */}
      {data.slice(1).map((_, rowIndex) => {
        const dataRowIndex = rowIndex + 1
        return (
          <div
            key={`row-${dataRowIndex}`}
            className={classNames(
              "sm:bg-transparent",
              getCellBackgroundClass(
                dataRowIndex,
                data.length,
                tableTheme,
                backgroundColor
              ),
              "flex flex-col gap-8 rounded-8 py-16 sm:py-0"
            )}
          >
            <Typography
              font="grotesk"
              weight="medium"
              color={getAccentColor(
                tableTheme === "light" ? Color.White : Color.Charcoal
              )}
              size="body-sm"
              text={rowHeadings[dataRowIndex]}
              className={classNames("px-16 sm:hidden")}
            />
            <div
              className={classNames(
                "grid w-full grid-flow-row items-center",
                actualColumnCount === 2
                  ? "grid-cols-2 sm:grid-cols-3"
                  : "grid-cols-3 sm:grid-cols-4"
              )}
            >
              <div
                className={classNames(
                  "mr-16 hidden h-full items-center rounded-8 sm:flex sm:py-16",
                  getCellBackgroundClass(
                    dataRowIndex,
                    data.length,
                    tableTheme,
                    backgroundColor
                  )
                )}
              >
                <Typography
                  font="grotesk"
                  weight="medium"
                  color={getAccentColor(
                    tableTheme === "light" ? Color.White : Color.Charcoal
                  )}
                  size="body-md"
                  text={rowHeadings[dataRowIndex]}
                  className="px-16"
                />
              </div>
              {Array.from({ length: actualColumnCount }, (_, colIndex) => (
                <CellData
                  key={`column-${colIndex}-row-${dataRowIndex}`}
                  rowIndex={dataRowIndex}
                  columnIndex={colIndex}
                  displayIndex={colIndex}
                  data={data}
                  rowHeadings={rowHeadings}
                  tableTheme={tableTheme}
                  backgroundColor={backgroundColor}
                  maxMobileColumns={maxMobileColumns}
                  maxDesktopColumns={maxDesktopColumns}
                  titleRowIndices={titleRowIndices}
                  className="justify-center !py-0 sm:!py-16"
                  variant="singleEntry"
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default SingleEntryComparisonTable
